import React, { memo } from 'react';
import { Input } from 'antd';
import { DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { Draggable } from 'react-beautiful-dnd';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import DragHandle from './DragHandle';
import DescriptionEditor from './DescriptionEditor';
import ItemLinkChip from './ItemLinkChip';
import SettingChip from './SettingChip';
import WhoSeesIt from './WhoSeesIt';
import { groupsLabel } from './checklistDraft';
import { itemDeleteWarning } from './checklistWarnings';

// Enter that confirms an IME candidate (keyCode 229) must not add an item.
const isComposing = e => e.nativeEvent.isComposing || e.keyCode === 229;

const ChecklistItemRow = props => {
	const { sectionID, item, index, event, groups, count, saved, autoFocus } = props;
	const { onChange, onDelete, onPickLink, onAddAfter } = props;
	const change = patch => onChange(sectionID, item.id, patch);
	const handleEnter = e => {
		if (!isComposing(e) && item.text.trim()) onAddAfter(sectionID, item.id);
	};
	const limited = item.group_ids.length > 0;
	const label = groupsLabel(groups, item.group_ids);
	return (
		<Draggable draggableId={item.id} index={index}>
			{provided => (
				<div ref={provided.innerRef} {...provided.draggableProps} className="ChecklistEditor-item">
					<DragHandle dragHandleProps={provided.dragHandleProps} label="Drag item" />
					<span className="ChecklistEditor-box" aria-hidden />
					<div className="ChecklistEditor-item-body">
						<Input
							bordered={false}
							value={item.text}
							placeholder="Item text"
							aria-label="Item text"
							autoFocus={autoFocus}
							onChange={e => change({ text: e.target.value })}
							onPressEnter={handleEnter}
						/>
						<DescriptionEditor
							label="Item description"
							value={item.description}
							onChange={description => change({ description })}
						/>
					</div>
					<ItemLinkChip
						onPress={item.on_press}
						onPick={() => onPickLink(sectionID, item.id)}
						onClear={() => change({ on_press: null })}
					/>
					<SettingChip
						icon={<TeamOutlined />}
						label={label}
						active={limited}
						compact={!limited}
						ariaLabel={limited ? `Who sees this item: ${label}` : 'Who sees this item'}
						title="Who sees this item?"
						content={
							<WhoSeesIt
								event={event}
								groupIDs={item.group_ids}
								everyoneLabel="Everyone who can see this section"
								onChange={ids => change({ group_ids: ids })}
							/>
						}
					/>
					{count > 0 && <span className="ChecklistEditor-checked">{count} checked</span>}
					<ConfirmDeleteButton
						type="text"
						size="small"
						icon={<DeleteOutlined />}
						aria-label="Delete item"
						className="ChecklistEditor-ghost"
						confirm={itemDeleteWarning(count, saved)}
						onDelete={() => onDelete(sectionID, item.id)}
					/>
				</div>
			)}
		</Draggable>
	);
};

export default memo(ChecklistItemRow);
